import { useMutation, useQuery } from "@apollo/client";
import { CREATE_UPDATE_BAND, DELETE_BAND, UPDATE_BAND } from "../graphql";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState, SyntheticEvent } from "react";
import { Control, Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import {
  Alert,
  Button,
  TextField,
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Grid,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Chip,
  Collapse,
} from "@mui/material";
import { type Crop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Band } from "../../../__generated__/graphql";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { TabbedPage } from "../../../components/TabbedPage";
import { Info } from "../../../components/Info";
import BandPerformances from "../performances";
import { artistDefaults, SocialLinks } from "../../post/SocialLinks";
import MarkdownEditor from "../../../components/MarkdownEditor";
import { ErrorRenderer } from "../../../components/ErrorRenderer";
import BandLinks from "../links/BandLinksList";
import { NavigationBlocker } from "../../../components/NavigationBlocker";
import { LinkButton } from "../../../components/LinkButton";
import { getUpdatedImageUrl } from "../../post/edit/helpers";
import { callCompressor } from "../imageEdit/Upload";
import { ImageSize } from "../../bands";
import { ImagePreview } from "../../../components/ImagePreview";
import { GET_FESTIVAL_SETTINGS } from "../../settings/graphql";
import { GET_ANNOUNCEMENTS } from "../../announcement/graphql";
import { CREATE_BOOKING } from "../../booking/graphql";
import { GenreInput, formatGenres, parseGenres } from "./GenreInput";
import dayjs from "dayjs";

const HTTPS_URL_REGEX = /^https:\/\/.+/i;

type BandButtonConfig = {
  text: string;
  url: string;
};

type BandButtonsValue = {
  config: BandButtonConfig[];
};

const normalizeBandButtons = (buttons: unknown): BandButtonsValue => {
  const rawConfig =
    buttons &&
    typeof buttons === "object" &&
    Array.isArray((buttons as { config?: unknown[] }).config)
      ? ((buttons as { config: unknown[] }).config ?? [])
      : Array.isArray(buttons)
        ? buttons
        : [];

  const config = rawConfig.map((entry) => ({
    text:
      entry &&
      typeof entry === "object" &&
      typeof (entry as { text?: unknown }).text === "string"
        ? (entry as { text: string }).text
        : "",
    url:
      entry &&
      typeof entry === "object" &&
      typeof (entry as { url?: unknown }).url === "string"
        ? (entry as { url: string }).url
        : "",
  }));

  return { config };
};

const schema = yup
  .object({
    name: yup.string().required(),
    description: yup.string().required(),
    showcase: yup.boolean(),
    imageUrl: yup.string().optional(),
    genres: yup.string().optional(),
    socials: yup.object({
      facebook: yup.string().url().optional(),
      tiktok: yup.string().url().optional(),
      twitter: yup.string().url().optional(),
      instagram: yup.string().url().optional(),
      website: yup.string().url().optional(),
      bandcamp: yup.string().url().optional(),
    }),
    buttons: yup.object({
      config: yup.array().of(
        yup.object({
          text: yup.string().trim().required("Button text is required"),
          url: yup
            .string()
            .trim()
            .url("Enter a valid URL")
            .matches(HTTPS_URL_REGEX, "URL must start with https://")
            .required("Button URL is required"),
        }),
      ),
    }).optional(),
  })
  .required();

type AnnouncementSelection = {
  id: string;
  name: string;
  availabilityDate?: string | null;
  editionId?: string | null;
};

type BandSearchResult = {
  id: string;
  name: string;
};

const getCurrentUserToken = () => {
  const token = localStorage.getItem("token");
  if (token) return JSON.parse(token);
  return undefined;
};

type NameMatchesWarningProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  bandId?: string;
  bandSearchBaseUrl: string;
  defaultValue?: string;
};

const NameMatchesWarning = ({
  control,
  bandId,
  bandSearchBaseUrl,
  defaultValue = "",
}: NameMatchesWarningProps) => {
  const [nameMatches, setNameMatches] = useState<BandSearchResult[]>([]);
  const [showNameMatches, setShowNameMatches] = useState(true);
  const bandName =
    useWatch({ control, name: "name", defaultValue }) ?? "";

  useEffect(() => {
    if (!bandSearchBaseUrl) return;

    const trimmedName = bandName.trim();
    if (trimmedName.length === 0) {
      setNameMatches([]);
      return;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      try {
        const normalizedBase = bandSearchBaseUrl.endsWith("/")
          ? bandSearchBaseUrl.slice(0, -1)
          : bandSearchBaseUrl;
        const url = `${normalizedBase}/search-bands/${encodeURIComponent(trimmedName)}`;
        const authorization = getCurrentUserToken();
        const authHeader = authorization
          ? { authorization: authorization }
          : {};
        const response = await fetch(url, {
          signal: controller.signal,
          headers: {
            ...authHeader,
          } as HeadersInit,
        });

        if (!response.ok) {
          throw new Error(`Search request failed: ${response.status}`);
        }
        const data = await response.json();
        if (controller.signal.aborted) return;
        const normalizedName = trimmedName.toLowerCase();
        const matches = Array.isArray(data)
          ? data.filter((item) => {
              if (!item || typeof item.name !== "string") return false;
              if (bandId && item.id === bandId) return false;
              return item.name.trim().toLowerCase().includes(normalizedName);
            })
          : [];
        setNameMatches(matches);
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error("Unable to check band name uniqueness", err);
          setNameMatches([]);
        }
      }
    }, 400);

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [bandId, bandName, bandSearchBaseUrl]);

  useEffect(() => {
    if (nameMatches.length === 0) {
      setShowNameMatches(true);
    }
  }, [nameMatches.length]);

  if (nameMatches.length === 0) return null;

  return (
    <Alert severity="warning" sx={{ mt: 1 }}>
      <Stack spacing={1}>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body2">
            A band with this name already exists.
          </Typography>
          <Button
            size="small"
            onClick={() => setShowNameMatches((previous) => !previous)}
            sx={{ whiteSpace: "nowrap" }}
          >
            {showNameMatches ? "Hide matches" : "Show matches"}
          </Button>
        </Stack>
        <Collapse in={showNameMatches}>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {nameMatches.map((match) => (
              <LinkButton
                key={match.id}
                path={`/band/${match.id}`}
                text={`View ${match.name}`}
                variant="outlined"
                style={{ textTransform: "none" }}
              />
            ))}
          </Stack>
        </Collapse>
      </Stack>
    </Alert>
  );
};

const BandForm = ({
  band,
  refetch,
}: {
  band: Band | undefined | null;
  refetch: (id: string) => void;
}) => {
  const navigate = useNavigate();
  const skipNavigationBlockRef = useRef(false);
  const { data: settingsData } = useQuery(GET_FESTIVAL_SETTINGS);
  const liveEditionId = settingsData?.settings?.[0]?.editionId;

  const {
    data: announcementsData,
    loading: announcementsLoading,
    refetch: refetchAnnouncements,
  } = useQuery(GET_ANNOUNCEMENTS, {
    variables: { editionId: liveEditionId ?? "" },
    skip: !liveEditionId,
  });

  useEffect(() => {
    console.log("resetting form");
    reset(
      {
        description: band?.description ?? "",
        name: band?.name ?? "",
        showcase: band?.showcase ?? false,
        imageUrl: band?.imageUrl ?? undefined,
        socials: band?.socials ?? artistDefaults,
        genres: formatGenres(parseGenres(band?.genres)),
        buttons: normalizeBandButtons(band?.buttons),
      },
      {
        keepDefaultValues: false,
        keepDirty: false,
        keepErrors: false,
        keepIsSubmitted: false,
        keepTouched: false,
      },
    );
  }, [band]);

  // const [updateBand, { loading: updateLoading, error }] = useMutation(
  //   UPDATE_BAND,
  //   {
  //     onCompleted: () => {
  //       toast("band updated");

  //       // navigate(`/band/${band.id}`, { replace: true });
  //     },
  //     // refetchQueries: [GET_BAND, GET_BANDS]
  //   }
  // );
  const [createUpdateBand, { loading: updateLoading, error }] = useMutation(
    CREATE_UPDATE_BAND,
    {
      onCompleted: (update) => {
        toast("Save complete");
        skipNavigationBlockRef.current = true;
        reset(
          {},
          {
            keepDirty: false,
          },
        );
        setTimeout(() => {
          navigate(`/band/${update.createUpdateBand.id}`);
        }, 0);
      },
      onError: (error) => {
        toast(JSON.stringify(error));
      },
    },
  );

  const [deleteBand] = useMutation(DELETE_BAND, {
    onCompleted: () => {
      toast("band deleted");
      navigate("/bands");
    },
  });
  const [createAnnouncementBooking, { loading: addToAnnouncementLoading }] =
    useMutation(CREATE_BOOKING, {
      onError: (mutationError) => {
        toast(mutationError.message);
      },
    });
  const bandSearchBaseUrl = process.env.REACT_APP_BAND_SEARCH_URL ?? "";
  const { id } = useParams();
  const [image, setImage] = useState<string>();
  // const [crop, setCrop] = useState<Crop>();
  const [crop, setCrop] = useState<Crop>({
    unit: "%", // Can be 'px' or '%'
    x: 25,
    y: 25,
    width: 50,
    height: 50,
  });
  const [announcementToAdd, setAnnouncementToAdd] =
    useState<AnnouncementSelection | null>(null);
  const [locallyAddedAnnouncements, setLocallyAddedAnnouncements] = useState<
    Set<string>
  >(new Set());
  const [isCompressing, setIsCompressing] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty },
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      description: band?.description ?? "",
      name: band?.name ?? "",
      showcase: band?.showcase ?? false,
      imageUrl: band?.imageUrl ?? undefined,
      socials: band?.socials ?? artistDefaults,
      genres: formatGenres(parseGenres(band?.genres)),
      buttons: normalizeBandButtons(band?.buttons),
    },
  });
  const {
    fields: buttonFields,
    append: appendButtonField,
    remove: removeButtonField,
  } = useFieldArray({
    control,
    name: "buttons.config",
  });
  const handleDelete = () => {
    if (band && band?.bookings?.length > 0) {
      confirmAlert({
        title: "Unable to delete band",
        message:
          "Please remove all bookings before attempting to delete this band.",
        buttons: [{ label: "Ok" }],
      });
      return;
    }

    confirmAlert({
      title: "Delete this Band?",
      message: "Are you sure? this can't be undone",
      buttons: [
        {
          label: "Yes",
          onClick: () => deleteBand({ variables: { bandId: band!.id } }),
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
  };

  const [imageUrl, setImageUrl] = useState<string | undefined>(
    band?.imageUrl ?? undefined,
  );
  const [tabIndex, setTabIndex] = useState(0);

  const location = useLocation();

  useEffect(() => {
    if (location?.state?.updatedImageUrl) {
      setImageUrl(location?.state?.updatedImageUrl);
      setValue("imageUrl", location?.state?.updatedImageUrl, {
        shouldValidate: false,
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  }, [location.state]);

  useEffect(() => {
    if (!band && tabIndex !== 0) {
      setTabIndex(0);
    }
  }, [band, tabIndex]);

  useEffect(() => {
    setLocallyAddedAnnouncements(new Set());
  }, [band?.id]);

  const onSubmit = (val: any) => {
    const { name, description, showcase, socials, genres, buttons } = val;
    const genresValue =
      typeof genres === "string" && genres.trim().length > 0
        ? genres.trim()
        : null;
    const buttonConfig = Array.isArray(buttons?.config)
      ? buttons.config
          .map((entry: { text?: string; url?: string }) => ({
            text: (entry?.text ?? "").trim(),
            url: (entry?.url ?? "").trim(),
          }))
          .filter((entry: BandButtonConfig) => entry.text && entry.url)
      : [];
    const buttonsValue = buttonConfig.length > 0 ? { config: buttonConfig } : null;

    createUpdateBand({
      variables: {
        id: band?.id,
        name,
        description,
        imageUrl: imageUrl !== "" ? imageUrl : null,
        showcase,
        socials,
        organisationId: process.env.REACT_APP_ORG_ID ?? "",
        genres: genresValue,
        buttons: buttonsValue,
      },
    });
  };

  const imageActionsMuted = !band?.id;
  const isCompressDisabled = !imageUrl || imageActionsMuted || isCompressing;

  const handleUpdateImage = () => {
    if (!band?.id) return;

    navigate("/image", {
      state: {
        bandId: band?.id,
      },
    });
  };

  const announcements = useMemo(() => {
    const list = announcementsData?.announcements ?? [];
    return [...list].sort((a, b) => {
      const aDate = a.availabilityDate ? dayjs(a.availabilityDate) : null;
      const bDate = b.availabilityDate ? dayjs(b.availabilityDate) : null;

      const aValid = aDate?.isValid();
      const bValid = bDate?.isValid();

      if (!aValid && !bValid) return 0;
      if (!aValid) return 1;
      if (!bValid) return -1;
      return aDate!.valueOf() - bDate!.valueOf();
    });
  }, [announcementsData]);

  const bandAnnouncementIds = useMemo(
    () =>
      new Set(
        (band?.bookings ?? [])
          .filter(
            (booking) =>
              booking?.announcement?.id &&
              (!liveEditionId ||
                booking?.announcement?.editionId === liveEditionId),
          )
          .map((booking) => booking.announcement!.id),
      ),
    [band?.bookings, liveEditionId],
  );

  const handleAnnouncementCheckbox = (announcementId: string) => {
    if (!band?.id) return;
    const announcement = announcements.find(
      (item) => item.id === announcementId,
    );
    if (!announcement) return;
    setAnnouncementToAdd({
      id: announcement.id,
      name: announcement.name,
      availabilityDate: announcement.availabilityDate,
      editionId: announcement.editionId,
    });
  };

  const handleCloseAnnouncementModal = () => setAnnouncementToAdd(null);

  const handleConfirmAddToAnnouncement = async () => {
    if (!announcementToAdd || !band?.id) return;

    try {
      await createAnnouncementBooking({
        variables: {
          bandId: band.id,
          announcementId: announcementToAdd.id,
          stageId: null,
          startTime: null,
          endTime: null,
          date: null,
        },
      });
      setLocallyAddedAnnouncements((prev) => {
        const updated = new Set(prev);
        updated.add(announcementToAdd.id);
        return updated;
      });
      toast("Band added to announcement");
      setAnnouncementToAdd(null);
      refetchAnnouncements?.();
      refetch(band.id);
    } catch (mutationError) {
      // handled by onError in mutation
    }
  };

  const imageKey = location?.state?.updated ?? "imageUrl";

  const headerActions = (
    <Stack
      direction="row"
      spacing={1.5}
      useFlexGap
      flexWrap="wrap"
      alignItems="center"
    >
      <Button type="submit" variant="contained" form="band-details-form">
        Save
      </Button>
      {band && (
        <Button variant="contained" color="error" onClick={handleDelete}>
          Delete band
        </Button>
      )}
      <LinkButton path="/band/" text="Create a new band" variant="contained" />
    </Stack>
  );

  const bandDetailsContent = (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        id="band-details-form"
        sx={{ display: "contents" }}
      >
        <Stack spacing={3}>
          <Card elevation={1}>
            <CardContent>
              <Grid container spacing={4} alignItems="flex-start">
                <Grid item xs={12} md={4}>
                  <Stack spacing={2}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                    >
                      Featured Image
                    </Typography>
                    <ImagePreview
                      key={imageKey}
                      src={getUpdatedImageUrl(imageUrl)}
                      alt="Band artwork"
                      onClick={band?.id ? handleUpdateImage : undefined}
                      sx={{
                        opacity: imageActionsMuted ? 0.5 : 1,
                        transition: "opacity 150ms ease",
                        pointerEvents: band?.id ? "auto" : "none",
                      }}
                    />
                    <ImageSize imageUrl={imageUrl} />
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <Button
                        variant="outlined"
                        onClick={handleUpdateImage}
                        disabled={imageActionsMuted}
                        sx={{
                          opacity: imageActionsMuted ? 0.6 : 1,
                          pointerEvents: imageActionsMuted ? "none" : "auto",
                        }}
                      >
                        Update Image
                      </Button>
                      <Button
                        variant="outlined"
                        color="secondary"
                        disabled={isCompressDisabled}
                        sx={{
                          opacity: imageActionsMuted ? 0.6 : 1,
                        }}
                        onClick={async () => {
                          if (!imageUrl || isCompressing) return;
                          setIsCompressing(true);
                          try {
                            const url = await callCompressor(imageUrl, true);
                            setImageUrl(url);
                            setValue("imageUrl", url, {
                              shouldValidate: false,
                              shouldDirty: true,
                              shouldTouch: true,
                            });
                          } finally {
                            setIsCompressing(false);
                          }
                        }}
                        startIcon={
                          isCompressing ? (
                            <CircularProgress size={16} />
                          ) : undefined
                        }
                      >
                        {isCompressing ? "Compressing..." : "Compress"}
                      </Button>
                    </Stack>
                    <TextField
                      id="band-image"
                      label="Image URL"
                      variant="outlined"
                      defaultValue={band?.imageUrl}
                      {...register("imageUrl")}
                      onChange={(value) => {
                        setImageUrl(value.target.value);
                      }}
                      fullWidth
                      margin="dense"
                      disabled={imageActionsMuted}
                      error={Boolean(errors.imageUrl)}
                      helperText={errors.imageUrl?.message}
                    />
                    <Controller
                      control={control}
                      name="genres"
                      render={({ field: { value, onChange } }) => (
                        <GenreInput value={value} onChange={onChange} />
                      )}
                    />
                    <Divider />
                    <Stack spacing={1}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                      >
                        Announcements
                      </Typography>
                      {band ? (
                        announcementsLoading ? (
                          <Typography variant="body2" color="text.secondary">
                            Loading announcements...
                          </Typography>
                        ) : !liveEditionId ? (
                          <Typography variant="body2" color="text.secondary">
                            Set a live edition in Settings to manage
                            announcements here.
                          </Typography>
                        ) : announcements.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No announcements for the live edition yet.
                          </Typography>
                        ) : (
                          <Stack spacing={0.5}>
                            {announcements.map((announcement) => {
                              const isChecked =
                                bandAnnouncementIds.has(announcement.id) ||
                                locallyAddedAnnouncements.has(announcement.id);
                              const availabilityText =
                                announcement.availabilityDate
                                  ? dayjs(announcement.availabilityDate).format(
                                      "YYYY/MM/DD HH:mm",
                                    )
                                  : "No availability date set";
                              const isLive =
                                announcement.availabilityDate &&
                                dayjs(announcement.availabilityDate).isBefore(
                                  dayjs(),
                                );

                              return (
                                <FormControlLabel
                                  key={announcement.id}
                                  control={
                                    <Checkbox
                                      checked={isChecked}
                                      onChange={(event) => {
                                        if (event.target.checked) {
                                          handleAnnouncementCheckbox(
                                            announcement.id,
                                          );
                                        }
                                      }}
                                      disabled={isChecked}
                                    />
                                  }
                                  label={
                                    <Stack spacing={0.4} width="100%">
                                      <Stack
                                        direction="row"
                                        spacing={1.5}
                                        alignItems="center"
                                        justifyContent="space-between"
                                      >
                                        <Typography fontWeight={600}>
                                          {announcement.name}
                                        </Typography>
                                        <Chip
                                          size="small"
                                          color={isLive ? "success" : "default"}
                                          variant="outlined"
                                          label={availabilityText}
                                          sx={{ fontWeight: 600 }}
                                        />
                                      </Stack>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ pl: 0.5 }}
                                      >
                                        {isLive
                                          ? "Already live in the app"
                                          : "Scheduled to appear when available"}
                                      </Typography>
                                    </Stack>
                                  }
                                />
                              );
                            })}
                          </Stack>
                        )
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Save the band before adding it to announcements.
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Stack spacing={3}>
                    <div>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          mb: 1,
                        }}
                      >
                        Band Details
                      </Typography>
                      <TextField
                        id="band-name"
                        label="Name"
                        variant="outlined"
                        defaultValue={band?.name}
                        {...register("name")}
                        fullWidth
                        margin="dense"
                        error={Boolean(errors.name)}
                        helperText={errors.name?.message}
                      />
                      <NameMatchesWarning
                        control={control}
                        bandId={band?.id}
                        bandSearchBaseUrl={bandSearchBaseUrl}
                        defaultValue={band?.name ?? ""}
                      />
                    </div>
                    <div>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          mb: 1,
                        }}
                      >
                        Description
                      </Typography>
                      <Controller
                        control={control}
                        name="description"
                        render={({ field: { onChange, value } }) => (
                          <MarkdownEditor
                            key={band?.id ?? "new"}
                            text={value}
                            onChange={onChange}
                          />
                        )}
                      />
                      {errors.description?.message && (
                        <Typography variant="caption" color="error">
                          {errors.description?.message}
                        </Typography>
                      )}
                    </div>
                    <div>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          mb: 1,
                        }}
                      >
                        Social Links
                      </Typography>
                      <Controller
                        control={control}
                        name="socials"
                        render={({
                          field: { onChange, onBlur, value, ref },
                        }) => (
                          <SocialLinks
                            socialType="ARTIST"
                            data={value}
                            // @ts-ignore
                            onChange={onChange}
                            errors={errors.socials}
                          />
                        )}
                      />
                    </div>
                    <div>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mb: 1 }}
                      >
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          sx={{
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          Buttons
                        </Typography>
                        <Button
                          type="button"
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            appendButtonField({
                              text: "",
                              url: "",
                            })
                          }
                        >
                          Add Button
                        </Button>
                      </Stack>
                      <Stack spacing={1.5}>
                        {buttonFields.length === 0 && (
                          <Typography variant="body2" color="text.secondary">
                            No buttons yet.
                          </Typography>
                        )}
                        {buttonFields.map((field, index) => (
                          <Card key={field.id} variant="outlined">
                            <CardContent sx={{ pb: "16px !important" }}>
                              <Stack spacing={1.5}>
                                <Grid container spacing={1.5}>
                                  <Grid item xs={12} sm={5}>
                                    <TextField
                                      label="Text"
                                      fullWidth
                                      {...register(`buttons.config.${index}.text`)}
                                      error={Boolean(
                                        errors.buttons?.config?.[index]?.text,
                                      )}
                                      helperText={
                                        errors.buttons?.config?.[index]?.text
                                          ?.message as string | undefined
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={7}>
                                    <TextField
                                      label="URL"
                                      placeholder="https://example.com"
                                      fullWidth
                                      {...register(`buttons.config.${index}.url`)}
                                      error={Boolean(
                                        errors.buttons?.config?.[index]?.url,
                                      )}
                                      helperText={
                                        (errors.buttons?.config?.[index]?.url
                                          ?.message as string | undefined) ??
                                        "Must start with https://"
                                      }
                                    />
                                  </Grid>
                                </Grid>
                                <Stack direction="row" justifyContent="flex-end">
                                  <Button
                                    type="button"
                                    color="error"
                                    size="small"
                                    onClick={() => removeButtonField(index)}
                                  >
                                    Remove
                                  </Button>
                                </Stack>
                              </Stack>
                            </CardContent>
                          </Card>
                        ))}
                      </Stack>
                    </div>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <ErrorRenderer errors={errors} />
        </Stack>
      </Box>
      <Info />
    </>
  );

  const performancesContent = band ? (
    <Stack spacing={3}>
      <Card elevation={1}>
        <CardContent>
          <Stack spacing={2}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
            >
              Performances
            </Typography>
            <BandPerformances
              bookings={band?.bookings ?? []}
              disableContainer
            />
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  ) : (
    <Typography variant="body2" color="text.secondary">
      Save the band before viewing performances.
    </Typography>
  );

  const linkedItemsContent = band ? (
    <Stack spacing={3}>
      <Card elevation={1}>
        <CardContent>
          <Stack spacing={2}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
            >
              Linked Items
            </Typography>
            <BandLinks band={band} disableContainer />
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  ) : (
    <Typography variant="body2" color="text.secondary">
      Save the band before managing linked items.
    </Typography>
  );

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const isSelectedAnnouncementLive =
    !!announcementToAdd?.availabilityDate &&
    dayjs(announcementToAdd.availabilityDate).isBefore(dayjs());
  const selectedAnnouncementDateText =
    announcementToAdd?.availabilityDate &&
    dayjs(announcementToAdd.availabilityDate).isValid()
      ? dayjs(announcementToAdd.availabilityDate).format("YYYY/MM/DD HH:mm")
      : "the availability date";

  return (
    <>
      <NavigationBlocker isDirty={isDirty} bypassRef={skipNavigationBlockRef} />
      <TabbedPage
        title={`${band ? "Edit" : "Create"} Band ${band?.name ?? ""}`}
        actions={headerActions}
        value={tabIndex}
        onChange={handleTabChange}
        tabs={[
          {
            label: "Band Details",
            id: "band-tab-0",
            ariaControlsId: "band-tabpanel-0",
            content: bandDetailsContent,
          },
          {
            label: "Performances",
            id: "band-tab-1",
            ariaControlsId: "band-tabpanel-1",
            content: performancesContent,
            disabled: !band,
          },
          {
            label: "Linked Items",
            id: "band-tab-2",
            ariaControlsId: "band-tabpanel-2",
            content: linkedItemsContent,
            disabled: !band,
          },
        ]}
      />
      <Dialog
        open={Boolean(announcementToAdd)}
        onClose={handleCloseAnnouncementModal}
      >
        <DialogTitle>Confirm announcement update</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {isSelectedAnnouncementLive
              ? "This announcement is already live, adding the band here will make it available in the app right away"
              : `The band will be available in the app on ${selectedAnnouncementDateText}`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseAnnouncementModal}
            disabled={addToAnnouncementLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAddToAnnouncement}
            variant="contained"
            disabled={addToAnnouncementLoading}
          >
            {addToAnnouncementLoading ? "Saving..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BandForm;
